"use client";

import React, { useState } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/feedback";
import { Label, Input, Select } from "@/components/ui/form-elements";
import {
  Mail,
  Briefcase,
  Layers,
  Edit2,
  Save,
  X,
  TrendingUp,
  UserCheck,
  Calendar,
  AlertTriangle,
  Bot,
  User,
  ShieldCheck,
  CheckCircle2,
  FolderGit,
  Network,
  ArrowUpRight,
} from "lucide-react";
import { updatePersonAction } from "@/actions/people-actions";
import { useBreadcrumbStore } from "@/components/layout/breadcrumb";
import { useRouter } from "next/navigation";

interface ProfileClientProps {
  person: any;
  dbProjects: any[];
  dbTasks: any[];
}

export function PersonProfileClient({
  person,
  dbProjects,
  dbTasks,
}: ProfileClientProps) {
  const setLabel = useBreadcrumbStore((state) => state.setLabel);
  React.useEffect(() => {
    if (person?.name) {
      setLabel(person.id, person.name);
    }
  }, [person?.id, person?.name, setLabel]);

  const [editMode, setEditMode] = useState(false);
  const [name, setName] = useState(person.name);
  const [role, setRole] = useState(person.role);
  const [team, setTeam] = useState(person.team);
  const [email, setEmail] = useState(person.email);
  const [specialty, setSpecialty] = useState(person.specialty);
  const [capacity, setCapacity] = useState(person.capacity.toString());

  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  // Mock fallback allocations if no real database projects/tasks exist
  const fallbackAllocations = [
    {
      id: "p1",
      title: "Quario Data Platform",
      role: "Project Owner",
      allocation: 40,
      status: "on_track",
    },
    {
      id: "p2",
      title: "Stripe Sandbox Integration",
      role: "Lead Integrator",
      allocation: 25,
      status: "watch",
    },
    {
      id: "p3",
      title: "Design System Implementation",
      role: "Contributor",
      allocation: person.capacity > 65 ? person.capacity - 65 : 15,
      status: "on_track",
    },
  ];

  const fallbackTasks = [
    {
      id: "t1",
      title: "Fix RLS Policy conflicts on client query session",
      priority: "critical",
      status: "in_progress",
    },
    {
      id: "t2",
      title: "Index vector embeddings for new PDF documents",
      priority: "high",
      status: "todo",
    },
  ];

  const router = useRouter();
  const [localAllocations, setLocalAllocations] = useState(() => {
    return dbProjects.length > 0 ? dbProjects : fallbackAllocations;
  });
  const [localTasks, setLocalTasks] = useState(() => {
    return dbTasks.length > 0 ? dbTasks : fallbackTasks;
  });

  const isAI = person.type === "AI";
  const numCapacity = parseInt(capacity, 10) || 0;
  const isBurnout = numCapacity > 90 || person.status === "burnout-risk";

  const handleSave = async () => {
    setLoading(true);
    setFeedback(null);
    try {
      const res = await updatePersonAction({
        id: person.id,
        name,
        role,
        team,
        email,
        specialty,
        capacity: numCapacity,
      });

      if (res.success) {
        setFeedback({
          type: "success",
          message:
            res.warning ||
            "Profilo aggiornato con successo nel grafo aziendale.",
        });
        setEditMode(false);
      } else {
        setFeedback({
          type: "error",
          message: "Errore durante il salvataggio dei dati.",
        });
      }
    } catch (err) {
      console.error(err);
      setFeedback({
        type: "error",
        message: "Errore di connessione durante l'aggiornamento.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Profile Summary banner */}
      <div className="flex flex-col md:flex-row md:items-center justify-between p-6 bg-card border border-border rounded-xl shadow-sm gap-4">
        <div className="flex items-center gap-4">
          <div
            className={`h-16 w-16 rounded-full flex items-center justify-center font-bold text-xl shadow-md select-none shrink-0 ${
              isAI
                ? "bg-category-innovation-soft border border-category-innovation-border text-category-innovation"
                : "bg-primary-600 text-text-on-primary"
            }`}
          >
            {person.avatar}
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-bold text-text-primary">{name}</h1>
              <Badge variant={isAI ? "innovation" : "people"}>
                {isAI ? "AI Agent" : "Human"}
              </Badge>
              <Badge variant={role === "owner" ? "default" : "neutral"}>
                {role}
              </Badge>
            </div>
            <p className="text-xs text-text-secondary mt-1">
              ID:{" "}
              <code className="bg-card-muted px-1.5 py-0.5 rounded text-[10px]">
                {person.id}
              </code>{" "}
              · Team: {team}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {!editMode ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditMode(true)}
              className="gap-1.5 h-9"
            >
              <Edit2 className="h-3.5 w-3.5" /> Modifica Profilo
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setEditMode(false);
                  setFeedback(null);
                }}
                className="gap-1 px-3 h-9"
                disabled={loading}
              >
                <X className="h-4 w-4" /> Annulla
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                className="gap-1.5 px-4 h-9"
                loading={loading}
              >
                <Save className="h-3.5 w-3.5" /> Salva Grafo
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Feedback Alerts */}
      {feedback && (
        <div
          className={`p-4 rounded-xl border flex items-start gap-3 ${
            feedback.type === "success"
              ? "bg-success-soft border-success/20 text-success"
              : "bg-error-soft border-error/20 text-error"
          }`}
        >
          <CheckCircle2 className="h-5 w-5 shrink-0 mt-0.5" />
          <div className="space-y-0.5">
            <h4 className="font-semibold text-sm">
              {feedback.type === "success"
                ? "Operazione riuscita"
                : "Impossibile salvare"}
            </h4>
            <p className="text-xs leading-relaxed opacity-90">
              {feedback.message}
            </p>
          </div>
        </div>
      )}

      {/* Main Grid content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column: Form / Info details */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Informazioni Collaboratore</CardTitle>
              <CardDescription>
                Gestisci ruoli aziendali e coordinate del grafo semantico.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {!editMode ? (
                // View Mode
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                  <div className="space-y-1">
                    <span className="text-xs font-semibold text-text-muted uppercase">
                      Nome Completo
                    </span>
                    <p className="font-medium text-text-primary text-base">
                      {name}
                    </p>
                  </div>

                  <div className="space-y-1">
                    <span className="text-xs font-semibold text-text-muted uppercase">
                      Email di riferimento
                    </span>
                    <p className="font-medium text-text-primary text-base flex items-center gap-1.5">
                      <Mail className="h-4 w-4 text-text-secondary" />
                      {email}
                    </p>
                  </div>

                  <div className="space-y-1">
                    <span className="text-xs font-semibold text-text-muted uppercase">
                      Team Appartenenza
                    </span>
                    <p className="font-medium text-text-primary text-base">
                      {team}
                    </p>
                  </div>

                  <div className="space-y-1">
                    <span className="text-xs font-semibold text-text-muted uppercase">
                      Specialità & Competenze
                    </span>
                    <p className="font-medium text-text-primary text-base flex items-center gap-1.5">
                      <Briefcase className="h-4 w-4 text-text-secondary" />
                      {specialty}
                    </p>
                  </div>

                  <div className="space-y-1">
                    <span className="text-xs font-semibold text-text-muted uppercase">
                      Ruolo sul Grafo
                    </span>
                    <p className="font-semibold text-text-primary text-base flex items-center gap-1.5">
                      <ShieldCheck className="h-4 w-4 text-primary-500" />
                      {role.toUpperCase()}
                    </p>
                  </div>

                  <div className="space-y-1">
                    <span className="text-xs font-semibold text-text-muted uppercase">
                      Entità Logica
                    </span>
                    <p className="font-semibold text-text-primary text-base flex items-center gap-1.5">
                      {isAI ? (
                        <>
                          <Bot className="h-4 w-4 text-category-innovation" />
                          Agente Artificiale (RAG Autonomo)
                        </>
                      ) : (
                        <>
                          <User className="h-4 w-4 text-category-people" />
                          Collaboratore Umano
                        </>
                      )}
                    </p>
                  </div>
                </div>
              ) : (
                // Edit Mode
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="edit-name">Nome Completo</Label>
                    <Input
                      id="edit-name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="edit-email">Email</Label>
                    <Input
                      id="edit-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="edit-team">Team Principale</Label>
                    <Select
                      id="edit-team"
                      value={team}
                      onChange={(e) => setTeam(e.target.value)}
                    >
                      <option value="Delivery Team">Delivery Team</option>
                      <option value="Product & Design">Product & Design</option>
                      <option value="Core Tech">Core Tech</option>
                      <option value="Growth & Sales">Growth & Sales</option>
                      <option value="Finance">Finance</option>
                      <option value="Unassigned">Unassigned</option>
                    </Select>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="edit-role">Ruolo Permessi</Label>
                    <Select
                      id="edit-role"
                      value={role}
                      onChange={(e) => setRole(e.target.value as any)}
                    >
                      <option value="owner">owner</option>
                      <option value="admin">admin</option>
                      <option value="member">member</option>
                      <option value="viewer">viewer</option>
                    </Select>
                  </div>

                  <div className="space-y-1.5 col-span-2">
                    <Label htmlFor="edit-specialty">
                      Specialità / Competenze chiavi
                    </Label>
                    <Input
                      id="edit-specialty"
                      value={specialty}
                      onChange={(e) => setSpecialty(e.target.value)}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="edit-capacity">
                      Capacità Carico Lavoro (%)
                    </Label>
                    <Input
                      id="edit-capacity"
                      type="number"
                      min="0"
                      max="100"
                      value={capacity}
                      onChange={(e) => setCapacity(e.target.value)}
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Allocation Details */}
          <Card>
            <CardHeader>
              <CardTitle>Dettaglio Allocazione & Progetti</CardTitle>
              <CardDescription>
                Carico di lavoro stimato per ogni progetto attivo legato a
                questa risorsa.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                {localAllocations.map((alloc) => (
                  <div
                    key={alloc.id}
                    onClick={() => router.push(`/work/projects/${alloc.id}`)}
                    className="relative flex items-center justify-between p-3.5 bg-card-muted/40 border border-border/40 rounded-xl hover:bg-card-muted/65 transition-all group cursor-pointer"
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center border border-primary-100 shrink-0">
                        <FolderGit className="h-4 w-4" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-text-primary text-sm">
                          {alloc.title}
                        </h4>
                        <p className="text-xs text-text-secondary mt-0.5">
                          {alloc.role}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 group-hover:opacity-0 transition-opacity">
                      <Badge
                        variant={
                          alloc.status === "on_track"
                            ? "success"
                            : alloc.status === "watch"
                              ? "warning"
                              : "error"
                        }
                      >
                        {alloc.status}
                      </Badge>
                      <div className="text-right">
                        <span className="font-bold text-text-primary text-sm">
                          {alloc.allocation}%
                        </span>
                        <p className="text-[10px] text-text-secondary font-medium uppercase tracking-wider">
                          workload
                        </p>
                      </div>
                    </div>

                    <div
                      className="absolute top-3 right-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity bg-card border border-border shadow-xs p-1 rounded-lg z-10"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        onClick={() =>
                          router.push(`/explore?nodeId=${alloc.id}`)
                        }
                        className="p-1 hover:bg-card-muted rounded text-text-secondary hover:text-primary-600 transition-colors"
                        title="Vedi nel Grafo"
                      >
                        <Network className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() =>
                          router.push(`/work/projects/${alloc.id}`)
                        }
                        className="p-1 hover:bg-card-muted rounded text-text-secondary hover:text-primary-600 transition-colors"
                        title="Dettaglio Progetto"
                      >
                        <ArrowUpRight className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => {
                          if (
                            confirm(
                              "Sei sicuro di voler rimuovere questa allocazione?",
                            )
                          ) {
                            setLocalAllocations((prev) =>
                              prev.filter((p) => p.id !== alloc.id),
                            );
                          }
                        }}
                        className="p-1 hover:bg-card-muted rounded text-text-secondary hover:text-error transition-colors"
                        title="Scollega"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column: Capacity Status & Tasks */}
        <div className="space-y-6">
          {/* Capacity gauge card */}
          <Card className={isBurnout ? "border-error/30 bg-error-soft/10" : ""}>
            <CardHeader>
              <CardTitle className="flex items-center gap-1.5">
                <Layers className="h-4 w-4 text-text-secondary" />
                Workload Capacity
              </CardTitle>
              <CardDescription>
                Allocazione totale delle ore lavorative.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-center py-4">
                <div
                  className={`text-4xl font-extrabold tracking-tight ${
                    isBurnout
                      ? "text-error"
                      : numCapacity > 75
                        ? "text-warning"
                        : "text-success"
                  }`}
                >
                  {numCapacity}%
                </div>
                <p className="text-xs text-text-secondary font-medium uppercase mt-1">
                  Carico Lavoro Totale
                </p>
              </div>

              {/* Progress bar */}
              <div className="space-y-2">
                <div className="w-full h-2.5 bg-border-strong/20 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      isBurnout
                        ? "bg-error"
                        : numCapacity > 75
                          ? "bg-warning"
                          : "bg-success"
                    }`}
                    style={{ width: `${numCapacity}%` }}
                  />
                </div>

                {isBurnout && (
                  <div className="flex items-start gap-2 bg-error-soft p-3 rounded-lg border border-error/20 text-xs text-error leading-relaxed">
                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                    <span>
                      <strong>Rischio Burnout rilevato:</strong> Questa risorsa
                      supera il 90% di allocazione attiva. Si consiglia di
                      ridistribuire alcuni blocker o task per prevenire
                      rallentamenti.
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Active Tasks card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-bold">
                Task in Corso nel Grafo
              </CardTitle>
              <CardDescription>
                I task correnti assegnati per la risoluzione.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <ul role="list" className="divide-y divide-border/30">
                {localTasks.map((task) => (
                  <li
                    key={task.id}
                    onClick={() => router.push(`/explore?nodeId=${task.id}`)}
                    className="relative p-4 space-y-1 hover:bg-card-muted/20 transition-all group cursor-pointer"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span className="text-xs font-semibold text-text-primary leading-tight line-clamp-2">
                        {task.title}
                      </span>
                      <Badge
                        variant={
                          task.priority === "critical" ||
                          task.priority === "high"
                            ? "error"
                            : "neutral"
                        }
                        className="scale-90 origin-right group-hover:opacity-0 transition-opacity"
                      >
                        {task.priority}
                      </Badge>
                    </div>
                    <div className="flex justify-between items-center text-[10px] text-text-secondary pt-1.5 group-hover:opacity-0 transition-opacity">
                      <span className="flex items-center gap-1">
                        <UserCheck className="h-3 w-3 text-success" />
                        {task.status}
                      </span>
                      <span className="flex items-center gap-1 font-medium">
                        <Calendar className="h-3 w-3" />
                        Scadenza Breve
                      </span>
                    </div>

                    <div
                      className="absolute top-3 right-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity bg-card border border-border shadow-xs p-1 rounded-lg z-10"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        onClick={() =>
                          router.push(`/explore?nodeId=${task.id}`)
                        }
                        className="p-1 hover:bg-card-muted rounded text-text-secondary hover:text-primary-600 transition-colors"
                        title="Vedi nel Grafo"
                      >
                        <Network className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => router.push(`/work/tasks`)}
                        className="p-1 hover:bg-card-muted rounded text-text-secondary hover:text-primary-600 transition-colors"
                        title="Tutti i Task"
                      >
                        <ArrowUpRight className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => {
                          if (
                            confirm(
                              "Sei sicuro di voler scollegare questo task?",
                            )
                          ) {
                            setLocalTasks((prev) =>
                              prev.filter((t) => t.id !== task.id),
                            );
                          }
                        }}
                        className="p-1 hover:bg-card-muted rounded text-text-secondary hover:text-error transition-colors"
                        title="Scollega"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
